import React,{useState,useMemo,useCallback,useEffect} from 'react';
import './smfgame.css';
import Navbar from '../Navbar/nbar';

/* ═══════════════════════════════════════════════════════
   SOMFIN FINANCIAL LIFE SIMULATOR v4
   Fixed-viewport game • Dashboard layout • Tab navigation
   No scrolling during gameplay • Persistent panels
   ═══════════════════════════════════════════════════════ */

const R=n=>Math.round(n);const fmt=n=>'\u20B9'+R(Math.abs(n)).toLocaleString('en-IN');
const rand=(a,b)=>Math.random()*(b-a)+a;const pick=a=>a[Math.floor(Math.random()*a.length)];
const clamp=(v,lo,hi)=>Math.max(lo,Math.min(hi,v));
const shuffle=a=>{const b=[...a];for(let i=b.length-1;i>0;i--){const j=Math.floor(Math.random()*(i+1));[b[i],b[j]]=[b[j],b[i]];}return b;};

const YEARS=10;const INF=0.06;

const CHARS=[
  {id:'priya',name:'Priya',full:'Priya Sharma',role:'Software Engineer',city:'Bangalore',salary:800000,age:22,growth:0.12,stability:0.85,cm:1.0,risk:'Moderate',skin:'#D4A574',hair:'#2C1810',shirt:'#6d3ba5'},
  {id:'arjun',name:'Arjun',full:'Arjun Patel',role:'Govt Officer',city:'Delhi',salary:500000,age:23,growth:0.06,stability:0.98,cm:0.85,risk:'Conservative',skin:'#C68642',hair:'#1A1A2E',shirt:'#2D6A4F'},
  {id:'meera',name:'Meera',full:'Meera Iyer',role:'Freelancer',city:'Mumbai',salary:600000,age:22,growth:0.18,stability:0.6,cm:1.15,risk:'Aggressive',skin:'#E8B98A',hair:'#3D1F00',shirt:'#B91C1C'},
];

const INST=[
  {id:'savings',name:'Savings A/C',minR:0.03,maxR:0.04,clr:'#8E90A6',rk:'Safe'},
  {id:'fd',name:'Fixed Deposit',minR:0.065,maxR:0.075,clr:'#A67C00',rk:'Safe'},
  {id:'ppf',name:'PPF',minR:0.071,maxR:0.071,clr:'#4f297e',rk:'Safe'},
  {id:'index',name:'Index Fund',minR:-0.18,maxR:0.30,clr:'#1A8A63',rk:'Med'},
  {id:'stocks',name:'Stocks',minR:-0.40,maxR:0.55,clr:'#C0392B',rk:'High'},
];

const EVENTS=[
  {t:'Medical emergency! Surgery needed.',mood:'worried',ch:[
    {l:'Pay \u20B91.5L from savings',e:{cash:-150000},r:'Emergency fund worked!',rm:'neutral'},
    {l:'Take loan at 14% APR',e:{debt:150000},r:'Debt trap begins.',rm:'sad'},
    {l:'Delay treatment',e:{hp:-20},r:'Health tanked. Big mistake.',rm:'sad'}]},
  {t:'Promotion offered! More money, more pressure.',mood:'surprised',ch:[
    {l:'Accept (+20% salary)',e:{salM:1.2,hp:-5},r:'Big raise! Stress is real though.',rm:'happy'},
    {l:'Negotiate (+12%, better balance)',e:{salM:1.12},r:'Best of both worlds.',rm:'happy'},
    {l:'Decline \u2014 stay comfortable',e:{},r:'Comfort zone. Peers are moving ahead.',rm:'neutral'}]},
  {t:'Family wedding! Expected to contribute big.',mood:'neutral',ch:[
    {l:'Grand \u2014 \u20B93L',e:{cash:-300000,sat:8},r:'Epic celebration!',rm:'celebrating'},
    {l:'Moderate \u2014 \u20B91L',e:{cash:-100000,sat:4},r:'Respectful. Smart.',rm:'happy'},
    {l:'Minimum \u2014 \u20B925K',e:{cash:-25000,sat:1},r:'Saved money. Relatives noticed.',rm:'worried'}]},
  {t:'Markets crashed 30%! Panic everywhere.',mood:'worried',ch:[
    {l:'Panic sell everything',e:{sellAll:true},r:'Locked in losses. Never do this.',rm:'sad'},
    {l:'Hold steady \u2014 don\u2019t touch',e:{mShock:-0.25},r:'Discipline. Markets recover.',rm:'neutral'},
    {l:'Buy the dip \u2014 invest \u20B950K more',e:{mShock:-0.25,bInv:50000},r:'Bold. History rewards this.',rm:'happy'}]},
  {t:'Laid off! Company downsizing.',mood:'sad',ch:[
    {l:'Take 2-month severance',e:{bonusCash:0.17,noInc:true},r:'No income this year. Ouch.',rm:'sad'},
    {l:'Negotiate 6-month package',e:{bonusCash:0.5,noInc:true},r:'Better deal. Still jobless.',rm:'worried'},
    {l:'Freelance immediately',e:{salM:0.7},r:'Income dropped but kept flowing.',rm:'neutral'}]},
  {t:'MBA opportunity! Career accelerator.',mood:'surprised',ch:[
    {l:'Full MBA \u2014 \u20B95L (+35% salary)',e:{cash:-500000,salM:1.35,edu:true},r:'Career transformed!',rm:'celebrating'},
    {l:'Online course \u2014 \u20B91.5L (+12%)',e:{cash:-150000,salM:1.12,edu:true},r:'Smart investment in yourself.',rm:'happy'},
    {l:'Skip \u2014 learn on the job',e:{},r:'Saved money. Growth is slower.',rm:'neutral'}]},
  {t:'Diwali! Festival spending season.',mood:'happy',ch:[
    {l:'Go all out \u2014 \u20B980K',e:{cash:-80000,sat:7},r:'Best Diwali ever!',rm:'celebrating'},
    {l:'Budget celebration \u2014 \u20B925K',e:{cash:-25000,sat:4},r:'Festive and smart.',rm:'happy'},
    {l:'Minimal \u2014 save everything',e:{cash:-5000,sat:1},r:'Future you is grateful. Present you is bored.',rm:'neutral'}]},
  {t:'Friend wants you to invest in their startup.',mood:'neutral',ch:[
    {l:'Invest \u20B92L \u2014 high risk',e:{cash:-200000,gamble:0.4},r:'Roll the dice...',rm:'worried'},
    {l:'Small stake \u2014 \u20B950K',e:{cash:-50000,gamble:0.6},r:'Manageable risk.',rm:'neutral'},
    {l:'Pass \u2014 too risky',e:{},r:'No risk, no reward. Safe.',rm:'neutral'}]},
  {t:'Landlord raised rent 25%!',mood:'worried',ch:[
    {l:'Pay it \u2014 location matters',e:{expUp:0.08},r:'Expensive but convenient.',rm:'neutral'},
    {l:'Move cheaper \u2014 \u20B930K cost',e:{cash:-30000,expUp:-0.05},r:'Painful move but saves monthly.',rm:'happy'},
    {l:'Negotiate 10% increase',e:{expUp:0.03},r:'Negotiation works!',rm:'happy'}]},
  {t:'Bull market! Everyone making money.',mood:'happy',ch:[
    {l:'Dump \u20B91L into stocks',e:{bInv:100000},r:'Riding the wave!',rm:'happy'},
    {l:'Increase SIP by \u20B930K',e:{bInv:30000},r:'Systematic. Smart.',rm:'happy'},
    {l:'Stay cautious \u2014 feels bubbly',e:{},r:'Missed gains. But also missed potential crash.',rm:'neutral'}]},
  {t:'Vehicle broke down. Needs major repair.',mood:'sad',ch:[
    {l:'Repair \u2014 \u20B935K',e:{cash:-35000},r:'Back on the road.',rm:'neutral'},
    {l:'Buy new \u2014 \u20B91.2L EMI',e:{debt:120000,sat:3},r:'Shiny! But those EMIs...',rm:'happy'},
    {l:'Public transport',e:{sat:-2},r:'Inconvenient. Builds character.',rm:'sad'}]},
  {t:'Tax refund! \u20B945K incoming.',mood:'happy',ch:[
    {l:'Save it all',e:{cash:45000},r:'Straight to savings.',rm:'happy'},
    {l:'Half save, half spend',e:{cash:22500,sat:3},r:'Balanced.',rm:'happy'},
    {l:'Treat yourself!',e:{cash:5000,sat:6},r:'YOLO. Or is it?',rm:'celebrating'}]},
];

const YEAR_CTX=['Fresh graduate. First salary ever.','Year 2. Finding your rhythm.','Lifestyle inflation begins.','Peers buying cars, getting married.','Halfway. Are you on track?','Career gets serious.','Compounding kicks in.','Habits are locking in.','Last chance to adjust.','Final year. What did you build?'];

const ACHS=[
  {id:'1l',n:'First Lakh',d:'NW > \u20B91L',t:s=>s.nw>=100000,i:'\uD83C\uDFC6'},
  {id:'div',n:'Diversified',d:'4+ instruments',t:s=>Object.values(s.p).filter(v=>v>0).length>=4,i:'\uD83C\uDF10'},
  {id:'nod',n:'Debt Free',d:'Zero debt',t:s=>s.debt<=0,i:'\u2705'},
  {id:'10l',n:'Lakhpati',d:'NW > \u20B910L',t:s=>s.nw>=1000000,i:'\uD83D\uDCB0'},
  {id:'em',n:'Safety Net',d:'6+ months cushion',t:s=>s.em>=6,i:'\uD83D\uDEE1\uFE0F'},
  {id:'ed',n:'Scholar',d:'Invested in education',t:s=>s.edu,i:'\uD83C\uDF93'},
  {id:'bal',n:'Balanced',d:'Satisfaction 35+',t:s=>s.sat>=35,i:'\u2696\uFE0F'},
  {id:'cr',n:'Crorepati',d:'NW > \u20B91Cr',t:s=>s.nw>=10000000,i:'\uD83D\uDC8E'},
];

/* ─── SVG CHARACTER ─── */
const Char=({mood='neutral',skin='#D4A574',hair='#2C1810',shirt='#6d3ba5'})=>{
  const eyes={happy:2,sad:2,worried:2,surprised:3,neutral:2,celebrating:2};
  const mouths={
    happy:'M-7-1Q0 7 7-1',sad:'M-6 3Q0-3 6 3',worried:'M-5 1Q0-1 5 1',
    surprised:'M-3 1Q0 5 3 1',neutral:'M-5 0L5 0',celebrating:'M-8 0Q0 9 8 0'
  };
  const armAngle=mood==='celebrating'?-40:0;
  return(
    <svg viewBox="-40 -70 80 150" className="g__char">
      <g><animateTransform attributeName="transform" type="translate" values="0,0;0,-2;0,0" dur="2.5s" repeatCount="indefinite"/>
      {/* legs */}<rect x="-11" y="42" width="9" height="22" rx="4" fill="#4A5568"/><rect x="2" y="42" width="9" height="22" rx="4" fill="#4A5568"/>
      {/* body */}<rect x="-15" y="12" width="30" height="34" rx="7" fill={shirt}/>
      {/* arms */}<rect x="-24" y="14" width="10" height="26" rx="5" fill={shirt} transform={`rotate(${armAngle} -19 27)`}/><rect x="14" y="14" width="10" height="26" rx="5" fill={shirt} transform={`rotate(${-armAngle} 19 27)`}/>
      {/* hands */}<circle cx={armAngle?-30:"-19"} cy={armAngle?8:"38"} r="5" fill={skin}/><circle cx={armAngle?30:"19"} cy={armAngle?8:"38"} r="5" fill={skin}/>
      {/* head */}<circle cx="0" cy="-10" r="18" fill={skin}/>
      {/* hair */}<ellipse cx="0" cy="-24" rx="18" ry="10" fill={hair}/><rect x="-18" y="-28" width="36" height="7" rx="3" fill={hair}/>
      {/* eyes */}<circle cx="-5" cy="-14" r={eyes[mood]||2} fill="#1E1E2D"/><circle cx="5" cy="-14" r={eyes[mood]||2} fill="#1E1E2D"/>
      {/* mouth */}<path d={mouths[mood]||mouths.neutral} fill="none" stroke="#1E1E2D" strokeWidth="1.5" strokeLinecap="round" transform="translate(0,-4)"/>
      {mood==='worried'&&<><path d="M-3-20L-1-22" stroke="#1E1E2D" strokeWidth="1" strokeLinecap="round"/><path d="M3-20L1-22" stroke="#1E1E2D" strokeWidth="1" strokeLinecap="round"/></>}
      </g>
    </svg>
  );
};

/* ─── PIE CHART ─── */
const Pie=({data})=>{
  const total=Object.values(data).reduce((a,b)=>a+b,0);
  if(total===0)return<div className="g__pie-empty">No investments</div>;
  let cum=0;
  return(
    <svg viewBox="0 0 100 100" className="g__pie">
      {INST.filter(i=>data[i.id]>0).map((inst,idx)=>{
        const f=data[inst.id]/total;const sa=cum;cum+=f*360;const ea=cum;const la=f>0.5?1:0;
        const r=42,cx=50,cy=50;
        const x1=cx+r*Math.cos((sa-90)*Math.PI/180),y1=cy+r*Math.sin((sa-90)*Math.PI/180);
        const x2=cx+r*Math.cos((ea-90)*Math.PI/180),y2=cy+r*Math.sin((ea-90)*Math.PI/180);
        return<path key={idx} d={`M${cx} ${cy}L${x1} ${y1}A${r} ${r} 0 ${la} 1 ${x2} ${y2}Z`} fill={inst.clr} stroke="#fff" strokeWidth="1.5" opacity="0.8"/>;
      })}
      <circle cx="50" cy="50" r="20" fill="#fff"/>
      <text x="50" y="52" textAnchor="middle" fontSize="7" fontWeight="700" fill="#1E1E2D">{fmt(total)}</text>
    </svg>
  );
};

/* ═══ GAME ═══ */
const FinLitGame=()=>{
  const[phase,setPhase]=useState('menu'); // menu|charPick|game|end
  const[ch,setCh]=useState(null);
  const[yr,setYr]=useState(1);
  const[tab,setTab]=useState('event'); // event|lifestyle|invest|shop
  const[sal,setSal]=useState(0);
  const[cash,setCash]=useState(0);
  const[debt,setDebt]=useState(0);
  const[port,setPort]=useState({savings:0,fd:0,ppf:0,index:0,stocks:0});
  const[ls,setLs]=useState('moderate');
  const[evts,setEvts]=useState([]);
  const[ei,setEi]=useState(0);
  const[evtDone,setEvtDone]=useState(false);
  const[result,setResult]=useState(null);
  const[mood,setMood]=useState('neutral');
  const[hp,setHp]=useState(100);
  const[sat,setSat]=useState(0);
  const[edu,setEdu]=useState(false);
  const[expM,setExpM]=useState(1);
  const[alloc,setAlloc]=useState({savings:40,fd:20,ppf:20,index:15,stocks:5});
  const[hist,setHist]=useState([]);
  const[achs,setAchs]=useState(new Set());
  const[turnDone,setTurnDone]=useState(false);
  const[toast,setToast]=useState(null);
  const[yearSum,setYearSum]=useState(null);

  const nw=useMemo(()=>cash+Object.values(port).reduce((a,b)=>a+b,0)-debt,[cash,port,debt]);
  const totInv=useMemo(()=>Object.values(port).reduce((a,b)=>a+b,0),[port]);
  const moExp=useMemo(()=>{const r={frugal:0.35,moderate:0.55,comfortable:0.75};return R(sal*(r[ls]||0.55)*(ch?.cm||1)*expM/12);},[sal,ls,ch,expM]);
  const emMonths=useMemo(()=>moExp>0?Math.floor((cash+(port.savings||0))/moExp):0,[cash,port,moExp]);

  const showToast=useCallback((msg,type='info')=>{setToast({msg,type});setTimeout(()=>setToast(null),3000);},[]);

  const startGame=useCallback(c=>{
    setCh(c);setSal(c.salary);setCash(R(c.salary*0.15));setDebt(0);
    setPort({savings:0,fd:0,ppf:0,index:0,stocks:0});setLs('moderate');
    setSat(0);setHp(100);setEdu(false);setExpM(1);setHist([]);setAchs(new Set());
    setAlloc({savings:40,fd:20,ppf:20,index:15,stocks:5});setYr(1);
    const ye=shuffle(EVENTS).slice(0,2);setEvts(ye);setEi(0);setEvtDone(false);
    setMood('happy');setTab('event');setTurnDone(false);setResult(null);setYearSum(null);
    setPhase('game');
  },[]);

  const handleChoice=useCallback(choice=>{
    const e=choice.e;
    if(e.cash)setCash(p=>Math.max(0,p+e.cash));
    if(e.debt)setDebt(p=>p+e.debt);
    if(e.salM)setSal(p=>R(p*e.salM));
    if(e.hp)setHp(p=>clamp(p+e.hp,10,100));
    if(e.sat)setSat(p=>p+e.sat);
    if(e.expUp)setExpM(p=>p+e.expUp);
    if(e.edu)setEdu(true);
    if(e.bInv){setCash(p=>Math.max(0,p-e.bInv));setPort(p=>({...p,index:(p.index||0)+e.bInv}));}
    if(e.sellAll){const t=Object.values(port).reduce((a,b)=>a+b,0);setCash(p=>p+R(t*0.7));setPort({savings:0,fd:0,ppf:0,index:0,stocks:0});showToast('Sold everything at 30% loss!','bad');}
    if(e.mShock)setPort(p=>({...p,index:Math.max(0,R(p.index*(1+e.mShock))),stocks:Math.max(0,R(p.stocks*(1+e.mShock*1.3)))}));
    if(e.noInc)setSal(p=>R(p*0.1));
    if(e.bonusCash)setCash(p=>p+R(sal*e.bonusCash));
    if(e.gamble){if(Math.random()<e.gamble){const win=R(Math.abs(e.cash||50000)*2);setCash(p=>p+win);showToast(`Gamble paid off! +${fmt(win)}`,'good');}else{showToast('Investment failed. Money lost.','bad');}}
    setResult(choice);setMood(choice.rm||'neutral');
  },[sal,port,showToast]);

  const nextEvent=useCallback(()=>{
    if(ei<evts.length-1){setEi(p=>p+1);setResult(null);setMood(evts[ei+1]?.mood||'neutral');}
    else{setEvtDone(true);setResult(null);setTab('lifestyle');setMood('neutral');}
  },[ei,evts]);

  const endTurn=useCallback(()=>{
    const rates={frugal:0.35,moderate:0.55,comfortable:0.75};
    const annExp=R(sal*(rates[ls]||0.55)*(ch?.cm||1)*expM);
    const tax=R(Math.max(0,sal-500000)*0.2);
    const emi=R(debt*0.22);const net=sal-annExp-tax-emi;
    const np={...port};let tRet=0;
    INST.forEach(i=>{const v=np[i.id]||0;if(v>0){const r=rand(i.minR,i.maxR);const g=R(v*r);tRet+=g;np[i.id]=Math.max(0,v+g);}});
    let nc=cash+net;const nd=Math.max(0,debt-emi);
    if(nc>0){const at=Object.values(alloc).reduce((a,b)=>a+b,0)||100;const inv=R(nc*0.6);
    Object.keys(alloc).forEach(k=>{np[k]=(np[k]||0)+R(inv*(alloc[k]/at));});nc=R(nc*0.4);}
    else{setDebt(p=>p+Math.abs(nc));nc=0;}
    const newNW=nc+Object.values(np).reduce((a,b)=>a+b,0)-nd;
    const rec={yr,sal,exp:annExp,tax,nw:newNW,hp,tRet};
    setHist(p=>[...p,rec]);setCash(nc);setPort(np);setDebt(nd);
    setSal(R(sal*(1+(ch?.growth||0.08))));setExpM(p=>p*(1+INF));
    setHp(p=>clamp(p-(ls==='frugal'?4:ls==='comfortable'?1:2),10,100));
    const st={nw:newNW,p:np,debt:nd,sat,em:emMonths,edu};const na=new Set(achs);
    ACHS.forEach(a=>{if(a.t(st)&&!na.has(a.id)){na.add(a.id);showToast(`Achievement: ${a.i} ${a.n}`,'good');}});
    setAchs(na);setYearSum(rec);setTurnDone(true);setMood(newNW>0?'happy':'worried');setTab('summary');
  },[sal,cash,debt,port,ls,ch,expM,alloc,yr,hp,sat,achs,emMonths,edu,showToast]);

  const nextYear=useCallback(()=>{
    if(yr>=YEARS){setPhase('end');return;}
    setYr(p=>p+1);const ye=shuffle(EVENTS).slice(0,2);setEvts(ye);setEi(0);
    setEvtDone(false);setResult(null);setTurnDone(false);setYearSum(null);
    setTab('event');setMood(ye[0]?.mood||'neutral');
  },[yr]);

  const score=useMemo(()=>{
    if(phase!=='end')return 0;
    return R(clamp(Math.min(30,(nw/(sal*3))*30)+Object.values(port).filter(v=>v>0).length*4+Math.min(15,sat*0.5)+hp*0.15+(debt===0?10:0)+(emMonths>=6?10:0),0,100));
  },[phase,nw,sal,port,sat,hp,debt,emMonths]);

  const grade=useMemo(()=>{
    if(score>=85)return{g:'A+',l:'Financial Master',c:'#1A8A63'};if(score>=70)return{g:'A',l:'Excellent',c:'#1A8A63'};
    if(score>=55)return{g:'B',l:'Good Foundation',c:'#4f297e'};if(score>=40)return{g:'C',l:'Needs Work',c:'#A67C00'};
    return{g:'D',l:'At Risk',c:'#C0392B'};
  },[score]);

  /* ═══ RENDER ═══ */
  return(
    <div className="g">
      <Navbar/>

      {/* ══ MENU ══ */}
      {phase==='menu'&&(
        <div className="g__menu">
          <div className="g__menu-body">
            <div className="g__menu-badge">SOMFIN GAME</div>
            <div className="g__menu-title">Financial Life Simulator</div>
            <div className="g__menu-sub">Live 10 years. Make real decisions. Every choice has consequences.</div>
            <div className="g__menu-stats">
              <span>10 Years</span><span>{'\u2022'}</span><span>12 Events</span><span>{'\u2022'}</span><span>5 Instruments</span><span>{'\u2022'}</span><span>8 Achievements</span>
            </div>
            <button className="g__menu-start" onClick={()=>setPhase('charPick')}>NEW GAME</button>
            <div className="g__menu-disc">For financial literacy only. Not investment advice.</div>
          </div>
        </div>
      )}

      {/* ══ CHARACTER PICK ══ */}
      {phase==='charPick'&&(
        <div className="g__pick">
          <div className="g__pick-title">Choose Your Character</div>
          <div className="g__pick-grid">
            {CHARS.map(c=>(
              <button key={c.id} className="g__pick-card" onClick={()=>startGame(c)}>
                <div className="g__pick-avatar"><Char mood="happy" skin={c.skin} hair={c.hair} shirt={c.shirt}/></div>
                <div className="g__pick-name">{c.full}</div>
                <div className="g__pick-role">{c.role} {'\u2022'} {c.city}</div>
                <div className="g__pick-meta">
                  <div><span>Salary</span><span>{fmt(c.salary)}</span></div>
                  <div><span>Growth</span><span>{(c.growth*100).toFixed(0)}%/yr</span></div>
                  <div><span>Stability</span><span>{(c.stability*100).toFixed(0)}%</span></div>
                  <div><span>Risk</span><span>{c.risk}</span></div>
                </div>
                <div className="g__pick-cta">PLAY {'\u2192'}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ══ GAME (FIXED VIEWPORT DASHBOARD) ══ */}
      {phase==='game'&&ch&&(
        <div className="g__board">
          {/* TOP BAR */}
          <div className="g__top">
            <div className="g__top-left">
              <span className="g__top-yr">Y{yr}</span>
              <span className="g__top-name">{ch.name}, {ch.age+yr-1}</span>
              <span className="g__top-ctx">{YEAR_CTX[yr-1]}</span>
            </div>
            <div className="g__top-right">
              <div className="g__top-prog"><div className="g__top-prog-f" style={{width:`${(yr/YEARS)*100}%`}}/></div>
              <span className="g__top-pct">{yr}/{YEARS}</span>
            </div>
          </div>

          {/* MAIN GRID: LEFT STATS | CENTER GAME | RIGHT PORTFOLIO */}
          <div className="g__grid">
            {/* LEFT PANEL */}
            <div className="g__left">
              <div className="g__panel-h">Dashboard</div>
              <div className="g__stat"><span className="g__stat-k">Cash</span><span className="g__stat-v">{fmt(cash)}</span></div>
              <div className="g__stat"><span className="g__stat-k">Salary</span><span className="g__stat-v">{fmt(sal)}/yr</span></div>
              <div className="g__stat"><span className="g__stat-k">Monthly Exp</span><span className="g__stat-v">{fmt(moExp)}</span></div>
              {debt>0&&<div className="g__stat g__stat--bad"><span className="g__stat-k">Debt</span><span className="g__stat-v">{fmt(debt)}</span></div>}
              <div className="g__stat g__stat--hl"><span className="g__stat-k">Net Worth</span><span className="g__stat-v">{fmt(nw)}</span></div>
              <div className="g__divider"/>
              <div className="g__bar-row"><span className="g__bar-k">{'\u2764\uFE0F'} Health</span><div className="g__bar-bg"><div className="g__bar-f" style={{width:`${hp}%`,background:hp>60?'#1A8A63':hp>30?'#A67C00':'#C0392B'}}/></div><span className="g__bar-v">{hp}</span></div>
              <div className="g__bar-row"><span className="g__bar-k">{'\uD83D\uDEE1\uFE0F'} E-Fund</span><div className="g__bar-bg"><div className="g__bar-f" style={{width:`${clamp(emMonths/12*100,0,100)}%`,background:emMonths>=6?'#1A8A63':emMonths>=3?'#A67C00':'#C0392B'}}/></div><span className="g__bar-v">{emMonths}m</span></div>
              <div className="g__bar-row"><span className="g__bar-k">{'\u2B50'} Satisfaction</span><div className="g__bar-bg"><div className="g__bar-f" style={{width:`${clamp(sat/60*100,0,100)}%`,background:'#4f297e'}}/></div><span className="g__bar-v">{sat}</span></div>
              <div className="g__divider"/>
              <div className="g__ach-mini">{ACHS.map(a=><span key={a.id} className={achs.has(a.id)?'g__ach-on':'g__ach-off'} title={a.n}>{a.i}</span>)}</div>
            </div>

            {/* CENTER */}
            <div className="g__center">
              {/* CHARACTER */}
              <div className="g__char-area"><Char mood={mood} skin={ch.skin} hair={ch.hair} shirt={ch.shirt}/></div>

              {/* TAB CONTENT */}
              <div className="g__content">
                {/* EVENT */}
                {tab==='event'&&!evtDone&&evts[ei]&&!result&&(
                  <div className="g__evt">
                    <div className="g__evt-tag">Event {ei+1}/{evts.length}</div>
                    <div className="g__evt-text">{evts[ei].t}</div>
                    <div className="g__evt-choices">{evts[ei].ch.map((c,i)=>(
                      <button key={i} className="g__evt-choice" onClick={()=>handleChoice(c)}>{c.l}</button>
                    ))}</div>
                  </div>
                )}
                {tab==='event'&&result&&(
                  <div className="g__evt">
                    <div className="g__evt-result">{result.r}</div>
                    <button className="g__act-btn" onClick={nextEvent}>{ei<evts.length-1?'Next Event':'Continue'} {'\u2192'}</button>
                  </div>
                )}

                {/* LIFESTYLE */}
                {tab==='lifestyle'&&(
                  <div className="g__decide">
                    <div className="g__decide-h">Choose Lifestyle</div>
                    <div className="g__ls-opts">
                      {[{id:'frugal',l:'Frugal',d:'35% of salary. Max savings.',e:'\uD83E\uDDCA'},{id:'moderate',l:'Moderate',d:'55%. Balanced.',e:'\u2696\uFE0F'},{id:'comfortable',l:'Comfortable',d:'75%. Good life.',e:'\u2728'}].map(x=>(
                        <button key={x.id} className={`g__ls-opt${ls===x.id?' g__ls-opt--on':''}`} onClick={()=>setLs(x.id)}><span>{x.e}</span><strong>{x.l}</strong><span className="g__ls-d">{x.d}</span></button>
                      ))}
                    </div>
                    <button className="g__act-btn" onClick={()=>setTab('invest')}>Set Investments {'\u2192'}</button>
                  </div>
                )}

                {/* INVEST */}
                {tab==='invest'&&(
                  <div className="g__invest">
                    <div className="g__decide-h">Allocate Savings</div>
                    {INST.map(i=>(
                      <div key={i.id} className="g__inv-row">
                        <span className="g__inv-nm" style={{color:i.clr}}>{i.name}</span>
                        <span className="g__inv-rk">{i.rk}</span>
                        <input type="range" min="0" max="100" step="5" value={alloc[i.id]} onChange={e=>setAlloc(p=>({...p,[i.id]:+e.target.value}))} style={{accentColor:i.clr}}/>
                        <span className="g__inv-pct">{alloc[i.id]}%</span>
                      </div>
                    ))}
                    <button className="g__act-btn g__act-btn--end" onClick={endTurn}>END YEAR {yr} {'\u2192'}</button>
                  </div>
                )}

                {/* SUMMARY */}
                {tab==='summary'&&yearSum&&(
                  <div className="g__summary">
                    <div className="g__decide-h">Year {yr} Complete</div>
                    <div className="g__sum-grid">
                      <div className="g__sum-s"><span>Income</span><span style={{color:'#1A8A63'}}>{fmt(yearSum.sal)}</span></div>
                      <div className="g__sum-s"><span>Expenses</span><span style={{color:'#C0392B'}}>{fmt(yearSum.exp)}</span></div>
                      <div className="g__sum-s"><span>Tax</span><span>{fmt(yearSum.tax)}</span></div>
                      <div className="g__sum-s"><span>Invest Returns</span><span>{fmt(yearSum.tRet)}</span></div>
                    </div>
                    <div className="g__sum-nw">Net Worth: {fmt(yearSum.nw)}</div>
                    <button className="g__act-btn" onClick={nextYear}>{yr>=YEARS?'SEE RESULTS':'YEAR '+(yr+1)+' \u2192'}</button>
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT PANEL */}
            <div className="g__right">
              <div className="g__panel-h">Portfolio</div>
              <Pie data={port}/>
              <div className="g__port-list">
                {INST.map(i=>(
                  <div key={i.id} className="g__port-row">
                    <span className="g__port-dot" style={{background:i.clr}}/>
                    <span className="g__port-nm">{i.name}</span>
                    <span className="g__port-val">{fmt(port[i.id]||0)}</span>
                  </div>
                ))}
              </div>
              <div className="g__divider"/>
              <div className="g__port-total"><span>Total Invested</span><span>{fmt(totInv)}</span></div>
            </div>
          </div>

          {/* TOAST */}
          {toast&&<div className={`g__toast g__toast--${toast.type}`}>{toast.msg}</div>}
        </div>
      )}

      {/* ══ END SCREEN ══ */}
      {phase==='end'&&ch&&(
        <div className="g__end">
          <div className="g__end-char"><Char mood={score>=55?'celebrating':'sad'} skin={ch.skin} hair={ch.hair} shirt={ch.shirt}/></div>
          <div className="g__end-badge">GAME OVER</div>
          <div className="g__end-title">Financial Report Card</div>
          <div className="g__score-ring">
            <svg viewBox="0 0 120 120" width={140} height={140}><circle cx="60" cy="60" r="50" fill="none" stroke="#E8E8F0" strokeWidth="8"/><circle cx="60" cy="60" r="50" fill="none" stroke={grade.c} strokeWidth="8" strokeDasharray={2*Math.PI*50} strokeDashoffset={2*Math.PI*50*(1-score/100)} strokeLinecap="round" transform="rotate(-90 60 60)" style={{transition:'stroke-dashoffset 1.5s ease'}}/></svg>
            <div className="g__score-in"><span style={{color:grade.c,fontSize:36,fontFamily:'Instrument Serif'}}>{grade.g}</span><span className="g__score-n">{score}/100</span></div>
          </div>
          <div className="g__score-lbl" style={{color:grade.c}}>{grade.l}</div>
          <div className="g__end-stats">
            <div><span>{fmt(nw)}</span><span>Net Worth</span></div>
            <div><span>{fmt(sal)}</span><span>Final Salary</span></div>
            <div><span>{hp}/100</span><span>Health</span></div>
            <div><span>{emMonths}mo</span><span>Emergency</span></div>
            <div><span>{debt>0?fmt(debt):'Zero'}</span><span>Debt</span></div>
            <div><span>{achs.size}/{ACHS.length}</span><span>Achievements</span></div>
          </div>
          {hist.length>0&&(
            <div className="g__end-chart"><div className="g__end-chart-h">Net Worth Journey</div><div className="g__end-bars">{hist.map((h,i)=>{const mx=Math.max(...hist.map(x=>Math.abs(x.nw)),1);return<div key={i} className="g__end-bar-col"><div className="g__end-bar" style={{height:`${(Math.abs(h.nw)/mx)*100}%`,background:h.nw>=0?'#1A8A63':'#C0392B'}}/><span>Y{h.yr}</span></div>;})}</div></div>
          )}
          <div className="g__end-achs"><div className="g__end-achs-h">Achievements</div><div className="g__end-ach-grid">{ACHS.map(a=><div key={a.id} className={`g__end-ach${achs.has(a.id)?' g__end-ach--on':''}`}><span>{a.i}</span><div>{a.n}</div></div>)}</div></div>
          <button className="g__menu-start" onClick={()=>{setPhase('charPick');setYr(1);}}>PLAY AGAIN</button>
          <div className="g__menu-disc">For financial literacy only. Not investment advice.</div>
        </div>
      )}
    </div>
  );
};

export default FinLitGame;